import { useState, useRef } from "react";
import axios from "axios";
import {
  Camera,
  PaperPlaneTilt,
  CheckCircle,
  Image as ImageIcon,
  X,
  WhatsappLogo,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { buildWhatsAppLink } from "../lib/brand";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MATERIAL_OPTIONS = [
  "Alumínio Bloco",
  "Latinhas",
  "Perfil de Alumínio",
  "Cobre Limpo",
  "Cobre Encapado",
  "Cabos / Sucata de Cobre",
  "Outro / Não tenho certeza",
];

export default function EvaluationForm() {
  const [form, setForm] = useState({
    nome: "",
    bairro: "",
    tipo_material: "",
    telefone: "",
  });
  const [photo, setPhoto] = useState(null); // {dataUrl, name}
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const fileRef = useRef(null);

  const handleChange = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("A foto precisa ter menos de 5MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) =>
      setPhoto({ dataUrl: ev.target.result, name: file.name });
    reader.readAsDataURL(file);
  };

  const clearPhoto = () => {
    setPhoto(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.bairro || !form.tipo_material) {
      toast.error("Preencha nome, bairro e tipo de material.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        nome: form.nome.trim(),
        bairro: form.bairro.trim(),
        tipo_material: form.tipo_material,
        telefone: form.telefone.trim() || null,
        foto_base64: photo?.dataUrl || null,
      });
      setSent(true);
      toast.success("Avaliação enviada! Vamos retornar pelo WhatsApp.");
    } catch (err) {
      console.error(err);
      toast.error("Não foi possível enviar agora. Tente o WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <section
        id="avaliacao"
        data-testid="evaluation-success"
        className="py-20 sm:py-28 border-t border-white/10"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="rc-grid-border bg-[var(--rc-surface)] p-8 sm:p-12 text-center">
            <div className="w-16 h-16 grid place-items-center bg-[var(--rc-whatsapp)]/15 border border-[var(--rc-whatsapp)]/40 text-[var(--rc-whatsapp)] mx-auto mb-6">
              <CheckCircle size={32} weight="duotone" />
            </div>
            <h2 className="font-display font-bold text-white text-3xl sm:text-4xl tracking-tight">
              Avaliação enviada!
            </h2>
            <p className="text-zinc-400 mt-3 max-w-lg mx-auto">
              Recebemos seus dados. Para acelerar o retorno, mande também
              a foto direto no nosso WhatsApp.
            </p>
            <a
              href={buildWhatsAppLink(
                `Olá! Acabei de enviar uma avaliação pelo site. Meu nome é ${form.nome}.`,
              )}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="evaluation-success-whatsapp"
              className="rc-btn-whatsapp inline-flex items-center justify-center gap-2 px-6 py-3.5 mt-7 rounded-md"
            >
              <WhatsappLogo size={20} weight="fill" />
              <span className="font-display font-bold">Falar no WhatsApp agora</span>
            </a>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="avaliacao"
      data-testid="evaluation-section"
      className="py-20 sm:py-28 border-t border-white/10 relative overflow-hidden"
    >
      <div className="absolute -top-32 -right-32 w-[420px] h-[420px] bg-[var(--rc-copper)]/10 rounded-full blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-16">
        <div>
          <div className="label-eyebrow mb-3">[ Avaliação rápida ]</div>
          <h2 className="font-display font-bold text-white text-3xl sm:text-5xl tracking-tight">
            Quatro campos.<br />
            <span className="text-[var(--rc-copper-bright)]">A gente avalia.</span>
          </h2>
          <p className="text-zinc-400 mt-5 leading-relaxed text-sm sm:text-base max-w-md">
            Preencha rápido aqui ou mande tudo direto no WhatsApp. Quanto mais
            clara a foto e a descrição, mais precisa fica a sua cotação.
          </p>

          <div className="mt-8 space-y-4">
            {[
              "Sua foto vai direto pro nosso avaliador",
              "Resposta em horário comercial",
              "Atendimento em Maringá e região",
            ].map((b, i) => (
              <div key={i} className="flex items-center gap-3 text-zinc-300">
                <span className="w-1.5 h-1.5 bg-[var(--rc-copper-bright)]" />
                <span className="text-sm">{b}</span>
              </div>
            ))}
          </div>

          <a
            href={buildWhatsAppLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="evaluation-side-whatsapp"
            className="mt-10 inline-flex items-center gap-2 text-[var(--rc-whatsapp)] hover:underline text-sm font-mono-ui"
          >
            <WhatsappLogo size={18} weight="fill" />
            ou clique aqui para falar no WhatsApp →
          </a>
        </div>

        <form
          onSubmit={handleSubmit}
          data-testid="evaluation-form"
          className="rc-grid-border bg-[var(--rc-surface)] p-6 sm:p-8 space-y-5"
        >
          <Field label="Nome" required>
            <input
              data-testid="evaluation-input-nome"
              type="text"
              value={form.nome}
              onChange={handleChange("nome")}
              placeholder="Como devemos te chamar?"
              className="rc-input"
              required
            />
          </Field>
          <Field label="Bairro / Cidade" required>
            <input
              data-testid="evaluation-input-bairro"
              type="text"
              value={form.bairro}
              onChange={handleChange("bairro")}
              placeholder="Ex.: Zona 7, Maringá"
              className="rc-input"
              required
            />
          </Field>
          <Field label="Tipo de material" required>
            <select
              data-testid="evaluation-input-material"
              value={form.tipo_material}
              onChange={handleChange("tipo_material")}
              className="rc-input"
              required
            >
              <option value="" disabled>
                Selecione o material
              </option>
              {MATERIAL_OPTIONS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </Field>
          <Field label="WhatsApp (opcional)">
            <input
              data-testid="evaluation-input-telefone"
              type="tel"
              value={form.telefone}
              onChange={handleChange("telefone")}
              placeholder="(44) 99999-9999"
              className="rc-input"
            />
          </Field>

          <Field label="Foto do material (recomendado)">
            {photo ? (
              <div className="relative rc-grid-border bg-black/40">
                <img
                  src={photo.dataUrl}
                  alt="prévia"
                  className="w-full max-h-64 object-cover"
                />
                <button
                  type="button"
                  onClick={clearPhoto}
                  data-testid="evaluation-clear-photo"
                  className="absolute top-2 right-2 w-9 h-9 grid place-items-center bg-black/80 border border-white/15 text-white hover:bg-black"
                  aria-label="Remover foto"
                >
                  <X size={16} weight="bold" />
                </button>
              </div>
            ) : (
              <label
                data-testid="evaluation-input-photo-label"
                className="cursor-pointer rc-grid-border bg-black/30 hover:bg-black/40 transition-colors p-6 flex flex-col items-center justify-center text-center"
              >
                <ImageIcon size={28} weight="duotone" className="text-[var(--rc-copper-bright)] mb-2" />
                <div className="text-sm font-medium text-white">
                  Toque para enviar uma foto
                </div>
                <div className="text-xs text-zinc-500 mt-1 font-mono-ui">
                  JPG · PNG · até 5MB
                </div>
                <input
                  ref={fileRef}
                  data-testid="evaluation-input-photo"
                  type="file"
                  accept="image/*"
                  onChange={handleFile}
                  className="hidden"
                />
              </label>
            )}
          </Field>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="submit"
              disabled={submitting}
              data-testid="evaluation-submit-btn"
              className="rc-btn-copper inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md disabled:opacity-60"
            >
              {submitting ? (
                <>Enviando...</>
              ) : (
                <>
                  <PaperPlaneTilt size={18} weight="bold" />
                  <span className="font-display font-bold">
                    Pedir avaliação
                  </span>
                </>
              )}
            </button>
            <a
              href={buildWhatsAppLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="evaluation-or-whatsapp"
              className="rc-btn-whatsapp inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md"
            >
              <WhatsappLogo size={18} weight="fill" />
              <span className="font-display font-bold">Ou pelo WhatsApp</span>
            </a>
          </div>
          <p className="text-[11px] text-zinc-500 leading-relaxed pt-2 font-mono-ui">
            Ao enviar, você concorda em ser contatado pela Ricatto Metais sobre sua avaliação.
          </p>
        </form>
      </div>

      <style>{`
        .rc-input {
          width: 100%;
          background: rgba(0,0,0,0.4);
          border: 1px solid var(--rc-border);
          color: #fff;
          padding: 14px 14px;
          font-family: "IBM Plex Sans", sans-serif;
          font-size: 15px;
          outline: none;
          transition: border-color 0.2s ease, background 0.2s ease;
        }
        .rc-input::placeholder { color: #71717a; }
        .rc-input:focus {
          border-color: var(--rc-copper-bright);
          background: rgba(0,0,0,0.6);
        }
        select.rc-input { appearance: none; background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none'><path d='M2 4l4 4 4-4' stroke='%23a1a1aa' stroke-width='1.5'/></svg>"); background-repeat: no-repeat; background-position: right 16px center; padding-right: 40px; }
      `}</style>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-mono-ui uppercase tracking-[0.18em] text-zinc-400 mb-2">
        {label} {required && <span className="text-[var(--rc-copper-bright)]">*</span>}
      </span>
      {children}
    </label>
  );
}
